import React, { useEffect, useState } from "react";
import { Card, Table, Spin, message, Row, Col, DatePicker, Select, Space, Tag } from "antd";
import API from "../../api";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { getUserRole } from "../../utils/auth";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

const SalesSummary = () => {
    const [data, setData] = useState(null);
    const [stores, setStores] = useState([]); // Store list for dropdown
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null); // Selected store name
    const [selectedFinancialYear, setSelectedFinancialYear] = useState(null); // Selected financial year
    const [rcInsuranceData, setRcInsuranceData] = useState(null); // RC Insurance Payment Details

    const userRoleValue = getUserRole();
    // Fetch stores for the dropdown
    const fetchStores = async () => {
        try {
            const response = await API.get("/stores");
            setStores(response?.data || []);
            
            // Set initial storeName based on userData
            const userData = JSON.parse(localStorage.getItem('userData')) || {};
            const userStoreId = userData.storeId;

            if (userRoleValue.role === 'admin') {
                // Admin users see "All Branches" by default
                setSelectedStore("All Branches");
            } else if (userStoreId) {
                // Non-admin users see their specific store
                const matchingStore = response.data.find((store) => store._id === userStoreId);
                if (matchingStore) {
                    setSelectedStore(matchingStore.name);
                }
            }
        } catch (error) {
            console.error("Error fetching stores:", error);
            message.error("Failed to fetch stores.");
        }
    };

    // Generate financial years starting from 2024-2025 to current financial year (April-based)
    const generateFinancialYears = () => {
        const currentDate = new Date();
        const financialYearStart = new Date(currentDate.getFullYear(), 3, 1); // April 1st

        let currentFinancialYearStart, currentFinancialYearEnd;
        if (currentDate < financialYearStart) {
            // If before April, the current financial year is the previous year
            currentFinancialYearStart = currentDate.getFullYear() - 1;
            currentFinancialYearEnd = currentDate.getFullYear();
        } else {
            // If April or later, the current financial year is this year
            currentFinancialYearStart = currentDate.getFullYear();
            currentFinancialYearEnd = currentDate.getFullYear() + 1;
        }

        const financialYears = [];
        let startYear = 2024; // Start from 2024
        let endYear = startYear + 1;

        // Generate financial years from 2024-2025 up to the current financial year
        while (startYear <= currentFinancialYearStart) {
            financialYears.push(`${startYear}-${endYear}`);
            startYear++;
            endYear++;
        }

        return financialYears; // Return in ascending order (oldest to newest)
    };

    useEffect(() => {
        fetchStores(); // Fetch stores on mount
        fetchSalesSummary(); // Fetch initial sales summary
    }, []);

    const fetchSalesSummary = async () => {
        try {
            setLoading(true);
            const params = {
                ...(selectedDate && !selectedFinancialYear && { // Only apply date range if no financial year is selected
                    fromDate: selectedDate.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                    toDate: selectedDate.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
                }),
                ...(selectedStore && { storeName: selectedStore }),
                ...(selectedFinancialYear && { financialYear: selectedFinancialYear }), // Add financialYear to params
            };

            const response = await API.get("/sales/all-sales", { params });
            setData(response.data);
            
            // Fetch RC Insurance Payment Details
            try {
                const rcInsuranceResponse = await API.get("/reports/rc-insurance-details", { params });
                setRcInsuranceData(rcInsuranceResponse.data);
            } catch (rcError) {
                console.error("Failed to fetch RC Insurance data:", rcError);
                // Don't show error message, just log it
            }
        } catch (error) {
            message.error("Failed to fetch sales data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSalesSummary(); // Refetch when date, store, or financial year changes
    }, [selectedDate, selectedStore, selectedFinancialYear]);

    const columns = [
        {
            title: "Payment Method",
            dataIndex: "_id",
            key: "paymentMethod",
            render: (text) => text || "Unknown",
        },
        {
            title: "Total Amount",
            dataIndex: "totalAmountByPaymentMethod",
            key: "totalAmountByPaymentMethod",
            render: (amount) => `₹ ${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0)}`,
        },
        {
            title: "Transaction Count",
            dataIndex: "count",
            key: "count",
            render: (count) => count || 0,
        },
    ];

    const handleDateChange = (date) => {
        setSelectedDate(date);
        if (date && selectedFinancialYear) {
            setSelectedFinancialYear(null); // Clear financial year if a specific date is selected
        }
    };

    const handleStoreChange = (value) => {
        setSelectedStore(value); // Update selected store
    };

    const handleFinancialYearChange = (value) => {
        setSelectedFinancialYear(value); // Update selected financial year
        if (value && selectedDate) {
            setSelectedDate(null); // Clear date if a financial year is selected
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", marginTop: 50 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{
            padding: "20px",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                <Col xs={24} sm={8} md={6} lg={5}>
                    <Select
                        placeholder="Select Branch"
                        value={selectedStore}
                        onChange={handleStoreChange}
                        style={{ width: "100%" }}
                        allowClear
                        loading={loading}
                        disabled={userRoleValue?.role !== 'admin'}
                        size="large"
                    >
                        <Option key="all-branches" value="All Branches">
                            All Branches
                        </Option>
                        {stores.map((store) => (
                            <Option key={store._id} value={store.name}>
                                {store.name}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col xs={24} sm={8} md={6} lg={5}>
                    <DatePicker
                        onChange={handleDateChange}
                        value={selectedDate}
                        format="YYYY-MM-DD"
                        placeholder="Select Date"
                        disabled={!!selectedFinancialYear}
                        style={{ width: "100%" }}
                        size="large"
                    />
                </Col>
                <Col xs={24} sm={8} md={6} lg={5}>
                    <Select
                        placeholder="Select Financial Year"
                        value={selectedFinancialYear}
                        onChange={handleFinancialYearChange}
                        style={{ width: "100%" }}
                        allowClear
                        loading={loading}
                        size="large"
                    >
                        {generateFinancialYears().map((year) => (
                            <Option key={year} value={year}>
                                {year}
                            </Option>
                        ))}
                    </Select>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={6}>
                    <Card
                        title="Total Paid Amount"
                        bordered
                        style={{
                            background: "#d1e7dd",
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            textAlign: "center",
                        }}
                    >
                        <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#155724" }}>
                            ₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(data?.totalPaidAmount || 0)}
                        </span>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card
                        title="Total Balance Amount"
                        bordered
                        style={{
                            background: "#f8d7da",
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            textAlign: "center",
                        }}
                    >
                        <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#721c24" }}>
                            ₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(data?.totalBalanceAmount || 0)}
                        </span>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card
                        title="Total Sales Amount"
                        bordered
                        style={{
                            background: "#cff4fc",
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            textAlign: "center",
                        }}
                    >
                        <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#055160" }}>
                            ₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(data?.totalAmount || 0)}
                        </span>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card
                        title="Total Sales Count"
                        bordered
                        style={{
                            background: "#fff3cd",
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            textAlign: "center",
                        }}
                    >
                        <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#856404" }}>
                            {data?.totalSalesCount || 0}
                        </span>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Card
                        title="Sales by Payment Method"
                        style={{
                            marginTop: 20,
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        }}
                    >
                        <Table
                            dataSource={data?.paymentSummary || []}
                            columns={columns}
                            rowKey="_id"
                            pagination={false}
                            bordered
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card
                        title="Pending Payment Collections"
                        style={{
                            marginTop: 20,
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        }}
                    >
                        <Table
                            dataSource={data?.pendingPaymentSummary || []}
                            columns={[
                                {
                                    title: "Payment Method",
                                    dataIndex: "_id",
                                    key: "paymentMethod",
                                    render: (text) => text || "Unknown",
                                },
                                {
                                    title: "Pending Amount",
                                    dataIndex: "totalPendingAmount",
                                    key: "totalPendingAmount",
                                    render: (amount) => `₹ ${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0)}`,
                                },
                                {
                                    title: "Pending Count",
                                    dataIndex: "count",
                                    key: "count",
                                    render: (count) => count || 0,
                                },
                            ]}
                            rowKey="_id"
                            pagination={false}
                            bordered
                        />
                    </Card>
                </Col>
            </Row>

            {/* RC Insurance Payment Details Section */}
            {rcInsuranceData && (
                <Card
                    title="RC Insurance Payment Details"
                    style={{
                        marginTop: 20,
                        borderRadius: "10px",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    }}
                >
                    {/* Summary Cards */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                        <Col span={6}>
                            <Card
                                title="Total Payments"
                                bordered
                                style={{
                                    background: "#e6f7ff",
                                    borderRadius: "10px",
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                    textAlign: "center",
                                }}
                            >
                                <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#1890ff" }}>
                                    {rcInsuranceData.summary?.totalPayments || 0}
                                </span>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card
                                title="Total Amount"
                                bordered
                                style={{
                                    background: "#f6ffed",
                                    borderRadius: "10px",
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                    textAlign: "center",
                                }}
                            >
                                <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#52c41a" }}>
                                    ₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rcInsuranceData.summary?.totalAmount || 0)}
                                </span>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card
                                title="Paid Amount"
                                bordered
                                style={{
                                    background: "#fff7e6",
                                    borderRadius: "10px",
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                    textAlign: "center",
                                }}
                            >
                                <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#fa8c16" }}>
                                    ₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rcInsuranceData.summary?.totalPaidAmount || 0)}
                                </span>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card
                                title="Balance Amount"
                                bordered
                                style={{
                                    background: "#fff2f0",
                                    borderRadius: "10px",
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                    textAlign: "center",
                                }}
                            >
                                <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#ff4d4f" }}>
                                    ₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rcInsuranceData.summary?.totalBalanceAmount || 0)}
                                </span>
                            </Card>
                        </Col>
                    </Row>

                    {/* RC Insurance Payment Collections Table */}
                    <Table
                        title={() => <h4>RC Insurance Payment Collections</h4>}
                        dataSource={rcInsuranceData.paymentMethodBreakdown || []}
                        columns={[
                            {
                                title: "Payment Method",
                                dataIndex: "_id",
                                key: "paymentMethod",
                                render: (method) => {
                                    const colors = {
                                        Cash: "green",
                                        "UPI/Bank": "blue"
                                    };
                                    return (
                                        <Tag color={colors[method] || "default"}>
                                            {method || "Unknown"}
                                        </Tag>
                                    );
                                },
                            },
                            {
                                title: "Transaction Count",
                                dataIndex: "count",
                                key: "count",
                            },
                            {
                                title: "Total Amount",
                                dataIndex: "totalAmount",
                                key: "totalAmount",
                                render: (amount) => `₹ ${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0)}`,
                            },
                            {
                                title: "Paid Amount",
                                dataIndex: "totalPaidAmount",
                                key: "totalPaidAmount",
                                render: (amount) => `₹ ${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0)}`,
                            },
                            {
                                title: "Balance Amount",
                                dataIndex: "totalBalanceAmount",
                                key: "totalBalanceAmount",
                                render: (amount) => `₹ ${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0)}`,
                            },
                        ]}
                        rowKey="_id"
                        pagination={false}
                        bordered
                        style={{ marginBottom: 20 }}
                    />

                </Card>
            )}
        </div>
    );
};

export default SalesSummary;